// src/services/userRolesService.js
// API functions for RBAC role management

export const fetchRoleOptions = async (apiCall) => {
  return await apiCall('GET', '/users/roles/options');
};

export const fetchInviteRoleOptions = async (apiCall) => {
  return await apiCall('GET', '/users/roles/invite-options');
};

export const fetchScopeOptions = async (apiCall) => {
  return await apiCall('GET', '/users/scopes/options');
};

export const fetchOrgHierarchy = async (apiCall) => {
  return await apiCall('GET', '/organizations/organization-hierarchy');
};

export const fetchUserRoles = async (apiCall, userId) => {
  return await apiCall('GET', `/users/${userId}/roles`);
};

export const updateUserRoles = async (apiCall, userId, roles) => {
  return await apiCall('POST', `/users/${userId}/roles`, { roles });
};

export const fetchAllUserRoles = async (apiCall) => {
  return await apiCall('GET', '/userroles');
};

export const fetchUserOrganizations = async (apiCall, userId) => {
  return await apiCall('GET', `/users/${userId}/organizations`);
};

// Tenant-scoped API functions
export const fetchTenantUsers = async (apiCall) => {
  return await apiCall('GET', '/tenantusers');
};

export const fetchAllTenantUserRoles = async (apiCall) => {
  return await apiCall('GET', '/tenantusers/roles');
};

export const fetchUserRolesTenant = async (apiCall, userId) => {
  return await apiCall('GET', `/tenantusers/${userId}/roles`);
};

export const updateUserRolesTenant = async (apiCall, userId, roles) => {
  return await apiCall('POST', `/tenantusers/${userId}/roles`, { roles });
};

export const fetchTenantRoleOptions = async (apiCall) => {
  return await apiCall('GET', '/tenantusers/roles/options');
};

export const fetchTenantScopeOptions = async (apiCall) => {
  return await apiCall('GET', '/tenantusers/scopes/options');
};

export const fetchUserOrganizationsTenant = async (apiCall, userId) => {
  return await apiCall('GET', `/tenantusers/${userId}/organizations`);
};

export const fetchTenantOrganizations = async (apiCall) => {
  return await apiCall('GET', '/tenantusers/organizations');
};

export const inviteTenantUser = async (apiCall, data) => {
  return await apiCall('POST', '/tenantusers/invite', data);
};

export const fetchTenantPendingInvites = async (apiCall) => {
  return await apiCall('GET', '/tenantusers/invites/pending');
};

export const fetchTenantInviteRoleOptions = async (apiCall) => {
  return await apiCall('GET', '/tenantusers/roles/invite-options');
};

// Higher-level invite role definitions matching backend UsersController invite-options
// Role values: OWNER=1, ADMIN=2, RISK_MANAGER=3, CONTRIBUTOR=4, APPROVER=5, VIEWER=6
// Scope values: ACCOUNT=1, TENANT=2, ORG=3, BUSINESS_UNIT=4, DEAL=5, SELF=6
export const ORG_INVITE_ROLES = [
  { key: 'ADMIN_ORG',         role: 2, scope: 3, displayName: 'Admin' },
  { key: 'RISK_MANAGER_ORG',  role: 3, scope: 3, displayName: 'Enterprise Risk Manager' },
  { key: 'RISK_MANAGER_SELF', role: 3, scope: 6, displayName: 'Risk Manager' },
  { key: 'CONTRIBUTOR_SELF',  role: 4, scope: 6, displayName: 'Contributor' },
  { key: 'APPROVER_SELF',     role: 5, scope: 6, displayName: 'Approver' },
  { key: 'VIEWER_ORG',        role: 6, scope: 3, displayName: 'Auditor' },
];

/**
 * Detect whether a user's roles match a single higher-level invite role.
 * @param {Array} roles - The user's current role assignments
 * @param {number} orgId - The current organization ID
 * @returns {{ matchedRole: object|null, extraRoles: Array, isAmbiguous: boolean }}
 */
export const detectHigherLevelRole = (roles, orgId) => {
  // Separate primary roles (ORG or SELF scope for this org) from extra roles (DEAL/BU scope)
  const primaryRoles = [];
  const extraRoles = [];

  for (const r of roles) {
    const scopeVal = r.scopeValue ?? r.scope;
    const entityId = r.scopeEntityId;
    // ORG scope (3) or SELF scope (6) with matching orgId → primary
    if ((scopeVal === 3 || scopeVal === 6) && entityId === orgId) {
      primaryRoles.push(r);
    } else {
      extraRoles.push(r);
    }
  }

  if (primaryRoles.length === 0) {
    return { matchedRole: null, extraRoles, isAmbiguous: false };
  }

  if (primaryRoles.length === 1) {
    const pr = primaryRoles[0];
    // OWNER (1) is a provisioning-only role (org founder / entity creator) that is never
    // separately invitable or assignable — surface it as ADMIN everywhere it is displayed.
    const rawRoleVal = pr.roleValue ?? pr.role;
    const roleVal = rawRoleVal === 1 ? 2 : rawRoleVal;
    const scopeVal = pr.scopeValue ?? pr.scope;
    const match = ORG_INVITE_ROLES.find(
      ir => ir.role === roleVal && ir.scope === scopeVal
    );
    if (match) {
      return { matchedRole: match, extraRoles, isAmbiguous: false };
    }
    // Single primary role but doesn't match any invite option
    return { matchedRole: null, extraRoles, isAmbiguous: true };
  }

  // Multiple primary roles → ambiguous
  return { matchedRole: null, extraRoles, isAmbiguous: true };
};
